import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import Landing from '@/pages/public/Landing';
import DniLookup from '@/pages/public/DniLookup';
import CustomerInviteRegister from '@/pages/public/CustomerInviteRegister';
import Pricing from '@/pages/public/Pricing';
import LegalPage from '@/pages/public/LegalPage';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';

import AdminLayout from '@/components/layout/AdminLayout';
import AdminDashboard from '@/pages/superadmin/Dashboard';
import AdminGyms from '@/pages/superadmin/Gyms';
import AdminOwners from '@/pages/superadmin/Owners';
import AdminPlans from '@/pages/superadmin/Plans';
import AdminAlumnos from '@/pages/superadmin/Alumnos';
import AdminPaymentSettings from '@/pages/superadmin/PaymentSettings';
import AdminSeoSettings from '@/pages/superadmin/SeoSettings';
import AdminOAuthSettings from '@/pages/superadmin/OAuthSettings';
import AdminSubscriptions from '@/pages/superadmin/Subscriptions';
import AdminMeasurements from '@/pages/superadmin/Measurements';
import AdminStoreOrders from '@/pages/superadmin/store/Orders';
import AdminNotifications from '@/pages/superadmin/Notifications';

import OwnerLayout from '@/components/layout/OwnerLayout';
import OwnerCreateGym from '@/pages/owner/CreateGym';
import OwnerDashboard from '@/pages/owner/Dashboard';
import OwnerCustomers from '@/pages/owner/Customers';
import OwnerCards from '@/pages/owner/Cards';
import OwnerClasses from '@/pages/owner/Classes';
import OwnerPayments from '@/pages/owner/Payments';
import OwnerGymProfile from '@/pages/owner/GymProfile';
import OwnerGyms from '@/pages/owner/Gyms';
import OwnerSubscription from '@/pages/owner/Subscription';
import OwnerPaymentSettings from '@/pages/owner/PaymentSettings';
import OwnerGymPlans from '@/pages/owner/GymPlans';
import OwnerMeasurements from '@/pages/owner/Measurements';
import OwnerMotivationalPhrases from '@/pages/owner/MotivationalPhrases';
import OwnerSatisfaction from '@/pages/owner/Satisfaction';
import OwnerRankings from '@/pages/owner/Rankings';
import OwnerTimer from '@/pages/owner/Timer';
import OwnerNotificationsSettings from '@/pages/owner/NotificationsSettings';
import OwnerStoreProducts from '@/pages/owner/store/Products';
import OwnerStoreOrders from '@/pages/owner/store/Orders';

import CustomerLayout from '@/components/layout/CustomerLayout';
import CustomerDashboard from '@/pages/customer/Dashboard';
import CustomerProfile from '@/pages/customer/Profile';
import CustomerCard from '@/pages/customer/Card';
import CustomerPayments from '@/pages/customer/Payments';
import CustomerClasses from '@/pages/customer/Classes';
import CustomerSeguimiento from '@/pages/customer/Seguimiento';
import CustomerHydration from '@/pages/customer/Hydration';
import CustomerSatisfaction from '@/pages/customer/Satisfaction';
import CustomerRankings from '@/pages/customer/Rankings';
import CustomerStore from '@/pages/customer/store/Store';
import CustomerMyOrders from '@/pages/customer/store/MyOrders';
import NotificationsSettings from '@/pages/customer/NotificationsSettings';
import CustomerTimer from '@/pages/customer/Timer';

function ProtectedRoute({ children, roles }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (roles && !roles.some(role => user.roles?.includes(role))) {
        return <Navigate to="/" replace />;
    }

    return children;
}

export default function AppRouter() {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/consulta-dni" element={<DniLookup />} />
            <Route path="/invitar/:token" element={<CustomerInviteRegister />} />
            <Route path="/precios" element={<Pricing />} />
            <Route path="/legal/privacidad" element={<LegalPage slug="privacidad" />} />
            <Route path="/legal/terminos" element={<LegalPage slug="terminos" />} />
            <Route path="/legal/pagos" element={<LegalPage slug="pagos" />} />
            <Route path="/legal/cookies" element={<LegalPage slug="cookies" />} />
            <Route path="/login" element={user ? <Navigate to={getDefaultRoute(user)} replace /> : <Login />} />
            <Route path="/register" element={user ? <Navigate to={getDefaultRoute(user)} replace /> : <Register />} />

            {/* Superadmin routes */}
            <Route path="/admin" element={<ProtectedRoute roles={['superadmin']}><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="alumnos" element={<AdminAlumnos />} />
                <Route path="gimnasios" element={<AdminGyms />} />
                <Route path="propietarios" element={<AdminOwners />} />
                <Route path="planes" element={<AdminPlans />} />
                <Route path="pagos" element={<AdminPaymentSettings />} />
                <Route path="suscripciones" element={<AdminSubscriptions />} />
                <Route path="seo" element={<AdminSeoSettings />} />
                <Route path="oauth" element={<AdminOAuthSettings />} />
                <Route path="mediciones" element={<AdminMeasurements />} />
                <Route path="tienda" element={<AdminStoreOrders />} />
                <Route path="notificaciones" element={<AdminNotifications />} />
            </Route>

            {/* Owner routes */}
            <Route path="/panel" element={<ProtectedRoute roles={['owner']}><OwnerLayout /></ProtectedRoute>}>
                <Route index element={<OwnerDashboard />} />
                <Route path="crear-gimnasio" element={<OwnerCreateGym />} />
                <Route path="clientes" element={<OwnerCustomers />} />
                <Route path="tarjetas" element={<OwnerCards />} />
                <Route path="clases" element={<OwnerClasses />} />
                <Route path="pagos" element={<OwnerPayments />} />
                <Route path="gimnasios" element={<OwnerGyms />} />
                <Route path="gimnasio" element={<OwnerGymProfile />} />
                <Route path="suscripciones" element={<OwnerSubscription />} />
                <Route path="configuracion-pagos" element={<OwnerPaymentSettings />} />
                <Route path="planes-alumnos" element={<OwnerGymPlans />} />
                <Route path="mediciones" element={<OwnerMeasurements />} />
                <Route path="satisfaccion" element={<OwnerSatisfaction />} />
                <Route path="rankings" element={<OwnerRankings />} />
                <Route path="frases" element={<OwnerMotivationalPhrases />} />
                <Route path="notificaciones" element={<OwnerNotificationsSettings />} />
                <Route path="timer" element={<OwnerTimer />} />
                <Route path="tienda/productos" element={<OwnerStoreProducts />} />
                <Route path="tienda/pedidos" element={<OwnerStoreOrders />} />
            </Route>

            {/* Customer routes */}
            <Route path="/mi-cuenta" element={<ProtectedRoute roles={['customer']}><CustomerLayout /></ProtectedRoute>}>
                <Route index element={<CustomerDashboard />} />
                <Route path="perfil" element={<CustomerProfile />} />
                <Route path="tarjeta" element={<CustomerCard />} />
                <Route path="pagos" element={<CustomerPayments />} />
                <Route path="clases" element={<CustomerClasses />} />
                <Route path="seguimiento" element={<CustomerSeguimiento />} />
                <Route path="hidratacion" element={<CustomerHydration />} />
                <Route path="satisfaccion" element={<CustomerSatisfaction />} />
                <Route path="rankings" element={<CustomerRankings />} />
                <Route path="tienda" element={<CustomerStore />} />
                <Route path="mis-pedidos" element={<CustomerMyOrders />} />
                <Route path="notificaciones" element={<NotificationsSettings />} />
                <Route path="timer" element={<CustomerTimer />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export function getDefaultRoute(user) {
    if (user.roles?.includes('superadmin')) return '/admin';

    if (user.roles?.includes('owner')) {
        const subscription = user.subscription;
        const isActive = subscription?.status === 'active';
        const isExpired = subscription?.ends_at && new Date(subscription.ends_at) < new Date();
        const isLifetime = subscription?.is_lifetime;
        const hasActiveSub = isActive && (!isExpired || isLifetime);

        if (!hasActiveSub) {
            return '/panel/suscripciones';
        }

        if (user.gyms?.length === 0) {
            return '/panel/crear-gimnasio';
        }

        return '/panel';
    }

    if (user.roles?.includes('customer')) return '/mi-cuenta';
    return '/';
}
